import { useCallback } from "react";
import { parsePresetPixelSize } from "../../../utils/getTransform";
import { DEFAULT_BACKGROUND, useEditorStore } from "../../../state/editorStore";
import { ensureBackgroundRemoved } from "../../../utils/ensureBackgroundRemoved";
import { fetchRemoteImageBlob } from "../../../utils/resolveCreationImageUrl";
import {
  fetchCreationImageBlob,
  fetchUploadImageBlob,
} from "../../../services/api/editor/background_removal";
import { restoreOverlaySourceLayout, tightenOverlayToSubject } from "../tightenOverlayToSubject";
import { urlToFile } from "../../../utils/urlToFile";
import { readBlobFromBlobUrl } from "../../../utils/adoptBlobUrl";
import { passportSuggestCrop, passportGenerate } from "../../../services/api/tools/passport_photo";
import { unsplashSearch, unsplashTrackDownload } from "../../../services/api/tools/unsplash";

function isRemoteHttpUrl(url) {
  return (
    typeof url === "string" &&
    /^https?:\/\//i.test(url) &&
    !url.startsWith("blob:") &&
    !url.startsWith("data:")
  );
}

/** True when a blob: URL is still readable (not revoked after clearImage). */
async function isLiveBlobUrl(url) {
  if (typeof url !== "string" || !url.startsWith("blob:")) return false;
  try {
    const blob = await readBlobFromBlobUrl(url);
    return Boolean(blob && blob.size > 0);
  } catch {
    return false;
  }
}

/**
 * Editor stage commands: background, remove-background, AI apply, passport
 * generate, and resize presets. Canvas painting stays in CanvasEditor.
 */
export function useEditorStageActions(ctx) {
  const {
    applyAiResult,
    applyAiTaskResult,
    applyManualEdit,
    background,
    bgRemovedImageUrl,
    bgColorTimeoutRef,
    bumpAiGeneration,
    canvasEditorRef,
    creationId,
    editorResizeMode,
    getAiLineage,
    getToken,
    inputPreview,
    isPassportFlow,
    lastResizePreset,
    layers,
    originalImageUrl,
    originalProcessedImage,
    originalSubjectUrlRef,
    processedImage,
    requireLoginForAi,
    resetBackground,
    selectedFile,
    selectedLayerId,
    setAppliedResizePresetId,
    setBackground,
    setBackgroundApplied,
    setBgRemovedImageUrl,
    setCanvasMeta,
    setCreationId,
    setEditorResizeMode,
    setIsCanvasPreloading,
    setLastResizePreset,
    setLayerVisibility,
    setOriginalProcessedImage,
    setPassportManualBusy,
    setPassportSuggestedCrop,
    setProcessedImage,
    setRemoveBackgroundActive,
    setShadow,
    studio,
    subjectUrlBeforeAiRef,
    transparentSourceId,
    updateImageLayer,
  } = ctx;

  const ensureBackgroundLayerVisible = useCallback(() => {
    const bgLayer = (useEditorStore.getState().layers || []).find(
      (l) => l.id === "background"
    );
    if (bgLayer && bgLayer.visible === false) {
      setLayerVisibility("background", true);
    }
  }, [setLayerVisibility]);

  const applyBackgroundColor = useCallback(async (color, options = {}) => {
    if (!color) return;

    const current = useEditorStore.getState().background;
    const sameColor =
      current?.type === "color" &&
      String(current.color || "").toLowerCase() === String(color).toLowerCase();
    if (sameColor && !options.force) {
      ensureBackgroundLayerVisible();
      return;
    }

    if (bgColorTimeoutRef.current) {
      clearTimeout(bgColorTimeoutRef.current);
      bgColorTimeoutRef.current = null;
    }

    // Color fill must not sit under a leftover stage image.
    canvasEditorRef.current?.removeBackgroundImage?.();

    setBackground(
      {
        type: "color",
        color,
        imageUrl: null,
        aiConfig: null,
        attribution: null,
      },
      { recordHistory: options.recordHistory !== false }
    );
    ensureBackgroundLayerVisible();
    setBackgroundApplied(true);
    setIsCanvasPreloading(false);
  }, [ensureBackgroundLayerVisible, setBackground]);

  const removeBackgroundColor = () => {
    console.log("=== Remove Background Color ===");
    
    // Cancel any pending background color application
    if (bgColorTimeoutRef.current) {
      clearTimeout(bgColorTimeoutRef.current);
      bgColorTimeoutRef.current = null;
    }
    
    // Start preloading state
    setIsCanvasPreloading(true);
    
    // Simulate 1 second preload delay
    bgColorTimeoutRef.current = setTimeout(() => {
      // Reset background state to no background
      resetBackground();
      setBackgroundApplied(false);
      setIsCanvasPreloading(false);
      console.log("Background color removed, reset to transparent");
      bgColorTimeoutRef.current = null;
    }, 1000);
  };

  const applyBackgroundImage = async (imageUrl, attribution = null) => {
    console.log("=== Apply Background Image ===");
    console.log("Image URL:", imageUrl);
    
    if (!imageUrl) {
      console.warn("[applyBackgroundImage] No imageUrl provided");
      return;
    }

    if (!canvasEditorRef.current) {
      console.warn("[applyBackgroundImage] CanvasEditor ref not available");
      return;
    }

    canvasEditorRef.current.setBackgroundImage(imageUrl);
    ensureBackgroundLayerVisible();

    // Persist stage bg immediately; image element loads async in CanvasEditor.
    setBackground({
      type: "image",
      color: "transparent",
      imageUrl: imageUrl,
      aiConfig: null,
      attribution: attribution || null,
    });
    setBackgroundApplied(true);
  };

  const removeBackgroundImage = async () => {
    console.log("=== Remove Background Image ===");

    if (!canvasEditorRef.current) {
      console.warn("[removeBackgroundImage] CanvasEditor ref not available");
      return;
    }

    // Start preloading state
    setIsCanvasPreloading(true);

    // Remove background image from CanvasEditor
    canvasEditorRef.current.removeBackgroundImage();

    // Simulate 1 second preload delay
    setTimeout(() => {
      // Reset background state to no background
      resetBackground();
      setIsCanvasPreloading(false);
      console.log("Background image removed");
    }, 1000);
  };

  const handleApplyAiBackground = (imageUrl) => {
    if (!requireLoginForAi()) return;
    applyBackgroundImage(imageUrl);
  };

  const handleAiBackgroundPromptGenerate = async (request) => {
    if (!requireLoginForAi()) return;
    if (studio.setError) studio.setError(null);
    const options =
      typeof request === "string" ? { prompt: request } : request || {};
    subjectUrlBeforeAiRef.current = processedImage || originalProcessedImage || null;
    const gen = bumpAiGeneration();
    try {
      const data = await studio.runAiBackgroundGenerate({
        ...options,
        outputFormat: options.outputFormat || "png",
      });
      if (data?.output_url) {
        await applyAiTaskResult({
          outputUrl: data.output_url,
          creationId: data.creation_id,
          background: {
            type: "ai",
            color: "transparent",
            imageUrl: null,
            aiConfig: {
              prompt: options.prompt || null,
              backgroundGuidanceImageUrl:
                options.backgroundGuidanceImageUrl || null,
            },
          },
          expectedGeneration: gen,
        });
      }
    } catch (_) {
      /* error surfaced via studio.error */
    }
  };

  const handleRestoreBackgroundRemoved = useCallback(async () => {
    let cutoutUrl = bgRemovedImageUrl || originalProcessedImage;
    if (!cutoutUrl) {
      console.warn("[handleRestoreBackgroundRemoved] No cutout URL stored");
      return;
    }

    if (
      typeof cutoutUrl === "string" &&
      /^https?:\/\//i.test(cutoutUrl) &&
      !cutoutUrl.startsWith("blob:") &&
      !cutoutUrl.startsWith("data:")
    ) {
      try {
        const token = await getToken();
        const store = useEditorStore.getState();
        const cid =
          store.transparentSourceId || store.creationId || null;
        if (token && cid) {
          try {
            const blob = await fetchCreationImageBlob(token, Number(cid));
            cutoutUrl = URL.createObjectURL(blob);
          } catch {
            cutoutUrl = await fetchRemoteImageBlob(cutoutUrl);
          }
        } else {
          cutoutUrl = await fetchRemoteImageBlob(cutoutUrl);
        }
        setBgRemovedImageUrl(cutoutUrl);
        useEditorStore.getState().setSubjectCutoutUrl?.(cutoutUrl);
      } catch (err) {
        console.error("[handleRestoreBackgroundRemoved] CORS blobify failed", err);
        alert(
          "Background-removed image could not be loaded for the editor (CORS). Please toggle Remove Background again."
        );
        return;
      }
    }

    setIsCanvasPreloading(true);

    setTimeout(() => {
      applyManualEdit({
        imageUrl: cutoutUrl,
      });
      setProcessedImage(cutoutUrl);
      setRemoveBackgroundActive(true);
      setIsCanvasPreloading(false);
    }, 300);
  }, [
    bgRemovedImageUrl,
    getToken,
    originalProcessedImage,
    setProcessedImage,
    applyManualEdit,
  ]);

  /**
   * Object-tab Remove BG toggle — main subject or selected uploaded image layer.
   */
  const handleRemoveBackgroundChange = useCallback(
    async (enabled) => {
      const layerState = useEditorStore.getState();
      const selId = layerState.selectedLayerId;
      const imageLayer =
        selId && selId !== "object" && selId !== "background"
          ? layerState.layers.find((l) => l.id === selId && l.type === "image")
          : null;

      // —— Uploaded overlay image ——
      if (imageLayer) {
        if (!enabled) {
          const revertUrl = imageLayer.sourceImageUrl || imageLayer.imageUrl;
          if (revertUrl) {
            setIsCanvasPreloading(true);
            updateImageLayer(imageLayer.id, {
              imageUrl: revertUrl,
              bgRemoved: false,
              ...restoreOverlaySourceLayout(imageLayer),
            });
            setIsCanvasPreloading(false);
          }
          return;
        }

        if (
          imageLayer.cutoutImageUrl &&
          imageLayer.cutoutImageUrl !==
            (imageLayer.sourceImageUrl || imageLayer.imageUrl)
        ) {
          setIsCanvasPreloading(true);
          try {
            const tight =
              (await tightenOverlayToSubject(
                imageLayer,
                imageLayer.cutoutImageUrl
              )) || {};
            updateImageLayer(imageLayer.id, {
              imageUrl: imageLayer.cutoutImageUrl,
              bgRemoved: true,
              sourceImageUrl: imageLayer.sourceImageUrl || imageLayer.imageUrl,
              ...tight,
            });
          } finally {
            setIsCanvasPreloading(false);
          }
          return;
        }

        setIsCanvasPreloading(true);
        try {
          const token = await getToken();
          const sourceUrl = imageLayer.sourceImageUrl || imageLayer.imageUrl;
          const assets = useEditorStore.getState().uploadAssets || [];
          const matchedAsset = assets.find(
            (a) =>
              a.rootCreationId != null &&
              (a.imageUrl === sourceUrl ||
                a.imageUrl === imageLayer.imageUrl ||
                a.output_url === sourceUrl)
          );
          const rootCreationId =
            imageLayer.rootCreationId ??
            matchedAsset?.rootCreationId ??
            matchedAsset?.root_creation_id ??
            null;

          const { cutoutUrl, creationId: cutoutCreationId } =
            await ensureBackgroundRemoved({
              token,
              imageUrl: sourceUrl,
              cachedCutoutUrl: null,
              rootCreationId,
              lineage: rootCreationId
                ? {
                    root_creation_id: rootCreationId,
                    parent_creation_id: rootCreationId,
                  }
                : null,
            });
          const tight =
            (await tightenOverlayToSubject(
              { ...imageLayer, rootCreationId },
              cutoutUrl
            )) || {};
          updateImageLayer(imageLayer.id, {
            sourceImageUrl: sourceUrl,
            cutoutImageUrl: cutoutUrl,
            imageUrl: cutoutUrl,
            bgRemoved: true,
            rootCreationId,
            ...(cutoutCreationId != null
              ? { creationId: cutoutCreationId }
              : {}),
            ...tight,
          });
        } catch (err) {
          console.error("[handleRemoveBackgroundChange] overlay BG removal failed", err);
          alert(err?.message || "Failed to remove background. Please try again.");
        } finally {
          setIsCanvasPreloading(false);
        }
        return;
      }

      // —— Main object ——
      if (!enabled) {
        setRemoveBackgroundActive(false);
        setIsCanvasPreloading(true);
        try {
          let revertUrl = null;
          const token = await getToken();
          const store = useEditorStore.getState();
          const rootId = store.rootCreationId;

          // 1) Live File (dashboard upload keeps this after BG remove).
          if (selectedFile instanceof File) {
            revertUrl = URL.createObjectURL(selectedFile);
          }

          // 2) Stored upload original via API — survives revoked blob: previews.
          if (!revertUrl && token && rootId) {
            try {
              const blob = await fetchUploadImageBlob(token, Number(rootId));
              if (blob && !(blob.type && String(blob.type).includes("json"))) {
                revertUrl = URL.createObjectURL(blob);
              }
            } catch (err) {
              console.warn(
                "[handleRemoveBackgroundChange] upload original fetch failed",
                err
              );
            }
          }

          // 3) Existing local preview only if the blob: URL is still alive.
          if (!revertUrl) {
            const candidates = [
              originalSubjectUrlRef.current,
              originalImageUrl,
              inputPreview,
            ];
            for (const candidate of candidates) {
              if (!candidate) continue;
              if (candidate.startsWith("blob:")) {
                if (await isLiveBlobUrl(candidate)) {
                  revertUrl = candidate;
                  break;
                }
                continue;
              }
              if (candidate.startsWith("data:")) {
                revertUrl = candidate;
                break;
              }
              if (isRemoteHttpUrl(candidate)) {
                try {
                  revertUrl = await fetchRemoteImageBlob(candidate);
                  break;
                } catch {
                  // try next
                }
              }
            }
          }

          if (!revertUrl) {
            alert("Original uploaded image is not available to restore.");
            setRemoveBackgroundActive(true);
            return;
          }

          originalSubjectUrlRef.current = revertUrl;

          // Fill canvas with the full upload — do not keep the cutout's tight BB.
          canvasEditorRef.current?.setForceFullFrameLayoutOnce?.();

          applyManualEdit({
            imageUrl: revertUrl,
            background: { ...DEFAULT_BACKGROUND },
          });
          setBackground({ ...DEFAULT_BACKGROUND });
          setBackgroundApplied(false);
          setProcessedImage(revertUrl);
        } catch (err) {
          console.error(
            "[handleRemoveBackgroundChange] restore original failed",
            err
          );
          alert(
            err?.message ||
              "Could not restore the uploaded image. Please try again."
          );
          setRemoveBackgroundActive(true);
        } finally {
          setIsCanvasPreloading(false);
        }
        return;
      }

      // Restore cached cutout only when it differs from the known original.
      // If the "cutout" is the same opaque original, force a fresh API call.
      const originalSource =
        originalSubjectUrlRef.current ||
        originalImageUrl ||
        inputPreview;
      if (
        bgRemovedImageUrl &&
        originalSource &&
        bgRemovedImageUrl !== originalSource
      ) {
        handleRestoreBackgroundRemoved();
        return;
      }

      setIsCanvasPreloading(true);
      setRemoveBackgroundActive(true);
      try {
        const token = await getToken();
        const store = useEditorStore.getState();
        const sourceUrl =
          originalSubjectUrlRef.current ||
          originalImageUrl ||
          inputPreview ||
          processedImage;
        if (
          sourceUrl &&
          !originalSubjectUrlRef.current &&
          !String(sourceUrl).includes("cutout")
        ) {
          originalSubjectUrlRef.current = sourceUrl;
        }

        const { cutoutUrl, creationId: cutoutCreationId } =
          await ensureBackgroundRemoved({
            token,
            imageFile: selectedFile,
            imageUrl: sourceUrl,
            // Force a real API run — do not reuse a stale opaque blob as the cutout.
            cachedCutoutUrl: null,
            rootCreationId: store.rootCreationId,
            creationId: store.transparentSourceId || store.creationId,
            lineage: store.rootCreationId
              ? {
                  root_creation_id: store.rootCreationId,
                  parent_creation_id: store.rootCreationId,
                }
              : null,
          });

        if (cutoutCreationId != null) {
          store.setLineage?.({
            transparentSourceId: Number(cutoutCreationId),
            creationId: Number(cutoutCreationId),
          });
        }

        setBgRemovedImageUrl(cutoutUrl);
        applyManualEdit({
          imageUrl: cutoutUrl,
        });
        setProcessedImage(cutoutUrl);
        setRemoveBackgroundActive(true);
      } catch (err) {
        console.error("[handleRemoveBackgroundChange] BG removal failed", err);
        setRemoveBackgroundActive(false);
        alert(err?.message || "Failed to remove background. Please try again.");
      } finally {
        setIsCanvasPreloading(false);
      }
    },
    [
      applyManualEdit,
      bgRemovedImageUrl,
      canvasEditorRef,
      getToken,
      handleRestoreBackgroundRemoved,
      inputPreview,
      originalImageUrl,
      originalSubjectUrlRef,
      processedImage,
      selectedFile,
      setBackground,
      setBackgroundApplied,
      setProcessedImage,
      updateImageLayer,
    ]
  );

  const removeAiBackground = useCallback(() => {
    const revertUrl = originalProcessedImage || subjectUrlBeforeAiRef.current;
    if (!revertUrl) {
      console.warn("[removeAiBackground] No stored cutout / subject URL to restore");
      return;
    }

    setIsCanvasPreloading(true);

    if (canvasEditorRef.current) {
      canvasEditorRef.current.removeBackgroundImage?.();
    }

    setTimeout(() => {
      applyManualEdit({
        imageUrl: revertUrl,
        background: { ...DEFAULT_BACKGROUND },
      });
      setProcessedImage(revertUrl);
      setIsCanvasPreloading(false);
      setBackgroundApplied(false);
    }, 300);
  }, [originalProcessedImage, setProcessedImage, applyManualEdit]);

  const handleUnsplashSearch = async (query) => {
    if (!requireLoginForAi()) return { images: [] };
    const token = await getToken();
    if (!token) throw new Error("Sign in required.");
    const data = await unsplashSearch(token, { query, per_page: 24 });
    if (!data?.success) {
      throw new Error(data?.detail || data?.message || "Search failed.");
    }
    return data;
  };

  const handleUnsplashTrackDownload = async (body) => {
    if (!requireLoginForAi()) throw new Error("Sign in required.");
    const token = await getToken();
    if (!token) throw new Error("Sign in required.");
    const data = await unsplashTrackDownload(token, body);
    // deferred = rate-limited soft skip; image already applied from DB URL
    if (data?.deferred) return data;
    if (!data?.success && data?.tracked !== true) {
      throw new Error(data?.detail || data?.message || "Tracking failed.");
    }
    return data;
  };

  const handleApplyAiShadowApi = async (shadowStyleId) => {
    if (!requireLoginForAi()) return;
    if (!shadowStyleId || shadowStyleId === "none") return;
    if (studio.setError) studio.setError(null);
    const gen = bumpAiGeneration();
    try {
      const data = await studio.runShadowApply(shadowStyleId);
      if (data?.shadow_url) {
        const displayUrl = await fetchRemoteImageBlob(data.shadow_url);
        // Keep subject size/position; tight BB on opaque subject; soft shadow
        // paints outside via no clipPath. Disable CSS object-shadow stacking.
        canvasEditorRef.current?.setPreserveObjectLayoutOnce?.();
        setShadow(
          {
            enabled: false,
            x: 0,
            y: 0,
            blur: 0,
            opacity: 1,
            color: "#000000",
          },
          { recordHistory: false }
        );
        const applied = applyAiResult({
          imageUrl: displayUrl,
          creationId: null,
          expectedGeneration: gen,
        });
        if (applied) {
          setProcessedImage(displayUrl);
          setOriginalProcessedImage(displayUrl);
          setCreationId(null);
          setRemoveBackgroundActive(false);
        }
      }
    } catch (_) {}
  };

  const handleApplyUpscale = async (opts) => {
    if (!requireLoginForAi()) return;
    if (studio.setError) studio.setError(null);
    const gen = bumpAiGeneration();
    try {
      const data = await studio.runUpscale(opts);
      if (data?.output_url) {
        await applyAiTaskResult({
          outputUrl: data.output_url,
          creationId: data.creation_id,
          expectedGeneration: gen,
          updateOriginal: true,
        });
      }
    } catch (_) {}
  };

  const handleApplyOutpaint = async (opts) => {
    if (!requireLoginForAi()) return;
    if (studio.setError) studio.setError(null);
    const gen = bumpAiGeneration();
    try {
      const data = await studio.runOutpaint(opts);
      if (data?.output_url) {
        await applyAiTaskResult({
          outputUrl: data.output_url,
          creationId: data.creation_id,
          expectedGeneration: gen,
        });
      }
    } catch (_) {}
  };

  const handleSuggestPassportCrop = async (opts) => {
    if (!requireLoginForAi()) return;
    if (studio.setError) studio.setError(null);
    setPassportManualBusy(true);
    try {
      if (isPassportFlow && (selectedFile || inputPreview)) {
        const token = await getToken();
        if (!token) throw new Error("Sign in required.");
        let file = selectedFile;
        if (!file && inputPreview) {
          file = await urlToFile(inputPreview, "passport-input.png");
        }
        const data = await passportSuggestCrop(token, file, opts);
        if (!data.success) {
          throw new Error(data.message || data.detail || "Passport suggest-crop failed.");
        }
        setPassportSuggestedCrop(data.result || null);
        return;
      }
      const result = await studio.runPassportSuggestCrop(opts);
      setPassportSuggestedCrop(result || null);
    } catch (_) {}
    finally {
      setPassportManualBusy(false);
    }
  };

  const handleGeneratePassport = async (opts) => {
    if (!requireLoginForAi()) return;
    if (studio.setError) studio.setError(null);
    const gen = bumpAiGeneration();
    setPassportManualBusy(true);
    try {
      let data;
      if (isPassportFlow && (selectedFile || inputPreview)) {
        const token = await getToken();
        if (!token) throw new Error("Sign in required.");
        let file = selectedFile;
        if (!file && inputPreview) {
          file = await urlToFile(inputPreview, "passport-input.png");
        }
        data = await passportGenerate(token, file, {
          ...opts,
          lineage: getAiLineage(),
        });
      } else {
        data = await studio.runPassportGenerate(opts);
      }
      if (data?.output_url) {
        await applyAiTaskResult({
          outputUrl: data.output_url,
          creationId: data.creation_id,
          expectedGeneration: gen,
          updateOriginal: true,
        });
      }
    } catch (_) {}
    finally {
      setPassportManualBusy(false);
    }
  };

  // Handle resize preset selection from ResizeControlMenu (FIT/FILL via applyPresetWithMode)
  const applyEditorResizePreset = useCallback(
    (preset, mode = "fit") => {
      if (!canvasEditorRef.current || !preset) return;

      const parsed = parsePresetPixelSize(preset);
      if (!parsed?.aspectRatio) {
        console.warn("[ResizePreset] Could not parse aspect ratio from preset:", preset);
        return;
      }

      setCanvasMeta({
        resolution: parsed.resolutionLabel || "",
        aspectRatio: parsed.aspectRatioLabel || "",
      });
      if (preset.id != null) setAppliedResizePresetId(preset.id);
      setLastResizePreset(preset);

      const imageUrl = processedImage || originalImageUrl || inputPreview;
      if (!imageUrl) {
        canvasEditorRef.current.resizeCanvasToAspectRatio(parsed.aspectRatio);
        return;
      }

      const applyWithSize = (iw, ih) => {
        canvasEditorRef.current?.setLayoutLock?.(true);
        const ok = canvasEditorRef.current?.applyPresetWithMode?.({
          aspectRatio: parsed.aspectRatio,
          mode: mode === "fill" ? "fill" : "fit",
          imageWidth: iw,
          imageHeight: ih,
        });
        if (!ok) {
          canvasEditorRef.current?.resizeCanvasToAspectRatio(parsed.aspectRatio);
        }
      };

      const img = new Image();
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        img.crossOrigin = "anonymous";
      }
      img.onload = () => {
        applyWithSize(img.naturalWidth || img.width, img.naturalHeight || img.height);
      };
      img.onerror = () => {
        canvasEditorRef.current?.resizeCanvasToAspectRatio(parsed.aspectRatio);
      };
      img.src = imageUrl;
    },
    [inputPreview, originalImageUrl, processedImage]
  );

  const handleResizePreset = useCallback(
    (preset) => {
      applyEditorResizePreset(preset, editorResizeMode);
    },
    [applyEditorResizePreset, editorResizeMode]
  );

  const handleEditorResizeModeChange = useCallback(
    (mode) => {
      setEditorResizeMode(mode);
      if (lastResizePreset) {
        applyEditorResizePreset(lastResizePreset, mode);
      }
    },
    [applyEditorResizePreset, lastResizePreset]
  );


  return {
    applyBackgroundColor,
    removeBackgroundColor,
    applyBackgroundImage,
    removeBackgroundImage,
    handleApplyAiBackground,
    handleAiBackgroundPromptGenerate,
    handleRestoreBackgroundRemoved,
    handleRemoveBackgroundChange,
    removeAiBackground,
    handleUnsplashSearch,
    handleUnsplashTrackDownload,
    handleApplyAiShadowApi,
    handleApplyUpscale,
    handleApplyOutpaint,
    handleSuggestPassportCrop,
    handleGeneratePassport,
    applyEditorResizePreset,
    handleResizePreset,
    handleEditorResizeModeChange,
  };
}
